/**
 * Main entry point for the Longbow helicopter simulation
 */

import * as THREE from 'three';
import { HelicopterPhysics } from './systems/HelicopterPhysics';
import { HelicopterController } from './systems/HelicopterController';
import { WeaponSystem, WeaponType } from './systems/WeaponSystem';
import { createHelicopter } from './components/Helicopter';
import { createTerrain } from './components/Terrain';
import { HUDRenderer } from './components/HUDRenderer';
import { ProjectileRenderer } from './components/ProjectileRenderer';
import { ExplosionEffect } from './components/ExplosionEffect';
import { MFDRenderer } from './components/MFDRenderer';
import { AudioSystem } from './components/AudioSystem';

class LongbowDemo {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private physics: HelicopterPhysics;
  private controller: HelicopterController;
  private weaponSystem: WeaponSystem;
  private helicopterMesh: THREE.Group;
  private hudRenderer: HUDRenderer;
  private mfdRenderer: MFDRenderer;
  private projectileRenderer: ProjectileRenderer;
  private explosionEffect: ExplosionEffect;
  private audioSystem: AudioSystem;
  private clock: THREE.Clock;
  private cameraMode: 'cockpit' | 'chase' | 'tads' = 'chase';
  private targetMeshes: Map<string, THREE.Group>;

  constructor() {
    // Initialize Three.js scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x295393); // Sky blue
    this.scene.fog = new THREE.Fog(0x97c6ff, 500, 3000);

    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      10000
    );

    // Renderer setup
    const container = document.getElementById('canvas-container');
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container?.appendChild(this.renderer.domElement);

    // Initialize systems
    this.physics = new HelicopterPhysics();
    this.controller = new HelicopterController(window.innerWidth, window.innerHeight);
    this.weaponSystem = new WeaponSystem();
    this.clock = new THREE.Clock();

    // Track target meshes
    this.targetMeshes = new Map();

    // Create scene elements
    this.setupLighting();
    this.helicopterMesh = createHelicopter();
    this.scene.add(this.helicopterMesh);

    const terrain = createTerrain();
    this.scene.add(terrain);

    // Add some targets for testing
    this.addTestTargets();

    // HUD
    this.hudRenderer = new HUDRenderer();

    // MFD displays
    const hudCanvas = document.getElementById('hud-canvas') as HTMLCanvasElement;
    this.mfdRenderer = new MFDRenderer(hudCanvas);
    this.mfdRenderer.setupTADSCamera(this.scene);

    // Projectile renderer
    this.projectileRenderer = new ProjectileRenderer(this.scene);

    // Explosion effects
    this.explosionEffect = new ExplosionEffect(this.scene);

    // Audio system
    this.audioSystem = new AudioSystem();

    // Initialize audio on first user interaction
    window.addEventListener('click', () => {
      if (!this.audioSystem.isInitialized()) {
        this.audioSystem.initialize();
        this.audioSystem.startRotor();
      }
    }, { once: true });

    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());

    // Start animation loop
    this.animate();
  }

  private setupLighting(): void {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Directional light (sun)
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(100, 200, 100);
    dirLight.castShadow = true;
    dirLight.shadow.camera.left = -500;
    dirLight.shadow.camera.right = 500;
    dirLight.shadow.camera.top = 500;
    dirLight.shadow.camera.bottom = -500;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 1000;
    this.scene.add(dirLight);
  }

  private addTestTargets(): void {
    // Add some simple target boxes
    const targetPositions = [
      { x: 200, z: -500 },
      { x: -300, z: -700 },
      { x: 100, z: -1000 },
    ];

    targetPositions.forEach((pos, i) => {
      // T-72 Tank (simple box representation)
      const hullGeometry = new THREE.BoxGeometry(8, 3, 12);
      const turretGeometry = new THREE.CylinderGeometry(2.5, 2.5, 2, 16);
      const barrelGeometry = new THREE.CylinderGeometry(0.3, 0.3, 8, 8);

      const tankMaterial = new THREE.MeshPhongMaterial({
        color: 0x2d4a2b, // Dark green
        flatShading: true,
      });

      const hull = new THREE.Mesh(hullGeometry, tankMaterial);
      hull.position.y = 1.5;
      hull.castShadow = true;

      const turret = new THREE.Mesh(turretGeometry, tankMaterial);
      turret.position.y = 4;
      turret.castShadow = true;

      const barrel = new THREE.Mesh(barrelGeometry, tankMaterial);
      barrel.rotation.z = Math.PI / 2;
      barrel.position.set(4, 4, 0);
      barrel.castShadow = true;

      const tank = new THREE.Group();
      tank.add(hull);
      tank.add(turret);
      tank.add(barrel);
      tank.position.set(pos.x, 0, pos.z);

      this.scene.add(tank);

      // Store tank mesh
      const targetId = `target_${i}`;
      this.targetMeshes.set(targetId, tank);

      // Add to weapon system
      this.weaponSystem.addTarget({
        id: targetId,
        position: { x: pos.x, y: 0, z: pos.z },
        designated: false,
        lockTime: 0,
      });
    });
  }

  private updateCamera(): void {
    const state = this.physics.getState();
    const pos = state.position;
    const rot = state.rotation;

    switch (this.cameraMode) {
      case 'cockpit':
        // First-person view
        this.camera.position.set(pos.x, pos.y + 2, pos.z);
        this.camera.rotation.set(rot.pitch, rot.yaw, rot.roll);
        break;

      case 'chase':
        // Third-person chase camera
        const distance = 30;
        const height = 15;
        const cameraX = pos.x - Math.sin(rot.yaw) * distance;
        const cameraZ = pos.z + Math.cos(rot.yaw) * distance;
        this.camera.position.set(cameraX, pos.y + height, cameraZ);
        this.camera.lookAt(pos.x, pos.y, pos.z);
        break;

      case 'tads':
        // TADS camera view (looking forward from nose)
        this.camera.position.set(pos.x, pos.y - 1, pos.z - 3);
        this.camera.rotation.set(rot.pitch, rot.yaw, 0);
        break;
    }
  }

  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.hudRenderer.resize(window.innerWidth, window.innerHeight);
    this.mfdRenderer.resize(window.innerWidth, window.innerHeight);
  }

  private animate = (): void => {
    requestAnimationFrame(this.animate);

    const deltaTime = Math.min(this.clock.getDelta(), 0.1); // Cap at 100ms

    // Update controller
    const controls = this.controller.update();

    // Handle weapon cycling and firing
    if (controls.cycleWeapon) {
      this.weaponSystem.cycleWeapon();
    }

    if (controls.fireWeapon) {
      const state = this.physics.getState();
      const projectile = this.weaponSystem.fire(
        Date.now(),
        state.position,
        state.rotation,
        { x: 0, y: 0, z: -1 } // Aim forward for now
      );

      // Play weapon sound
      if (projectile) {
        const weaponType = this.weaponSystem.getCurrentWeapon().type;
        switch (weaponType) {
          case WeaponType.CHAIN_GUN:
            this.audioSystem.playChainGun();
            break;
          case WeaponType.ROCKETS:
            this.audioSystem.playRocketLaunch();
            break;
          case WeaponType.HELLFIRE:
            this.audioSystem.playHellfireLaunch();
            break;
        }
      }
    }

    // Handle target designation
    if (controls.designateTarget) {
      // Designate closest target in view
      const targets = this.weaponSystem.getTargets();
      const state = this.physics.getState();
      
      if (targets.length > 0) {
        // Find closest target
        let closestTarget = targets[0];
        let minDist = Infinity;
        
        targets.forEach((target) => {
          const dx = target.position.x - state.position.x;
          const dz = target.position.z - state.position.z;
          const dist = Math.sqrt(dx * dx + dz * dz);
          if (dist < minDist) {
            minDist = dist;
            closestTarget = target;
          }
        });
        
        this.weaponSystem.designateTarget(closestTarget.id);
      }
    }

    if (controls.cycleView) {
      this.cameraMode =
        this.cameraMode === 'chase'
          ? 'cockpit'
          : this.cameraMode === 'cockpit'
          ? 'tads'
          : 'chase';
    }

    // Update physics
    this.physics.update(deltaTime, {
      collective: controls.collective,
      cyclicPitch: controls.cyclicPitch,
      cyclicRoll: controls.cyclicRoll,
      yaw: controls.yaw,
    });

    // Update audio based on helicopter state
    const state = this.physics.getState();
    this.audioSystem.updateRotorPitch(state.collective);

    // Update weapon system projectiles
    this.weaponSystem.updateProjectiles(deltaTime);

    // Check for projectile collisions
    this.checkProjectileCollisions();

    // Update projectile rendering
    this.projectileRenderer.update(this.weaponSystem.getProjectiles());

    // Update explosions
    this.explosionEffect.update(deltaTime);

    // Update helicopter mesh position
    this.helicopterMesh.position.set(state.position.x, state.position.y, state.position.z);
    this.helicopterMesh.rotation.set(state.rotation.pitch, state.rotation.yaw, state.rotation.roll);

    // Update camera
    this.updateCamera();

    // Render scene
    this.renderer.render(this.scene, this.camera);

    // Render HUD
    this.hudRenderer.render(state, this.weaponSystem.getWeaponStatus());

    // Render MFDs
    this.mfdRenderer.render(state, this.weaponSystem.getTargets(), state.rotation);
  };

  private checkProjectileCollisions(): void {
    const projectiles = this.weaponSystem.getProjectiles();

    projectiles.forEach((projectile) => {
      // Check collision with ground
      if (projectile.position.y <= 0) {
        this.explosionEffect.createExplosion(projectile.position, 2);
        this.audioSystem.playExplosion();
        projectile.active = false;
        return;
      }

      // Check collision with targets
      this.weaponSystem.getTargets().forEach((target) => {
        const dx = projectile.position.x - target.position.x;
        const dy = projectile.position.y - target.position.y;
        const dz = projectile.position.z - target.position.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        // Collision radius
        if (distance < 5) {
          this.explosionEffect.createExplosion(projectile.position, 3);
          this.audioSystem.playExplosion();
          projectile.active = false;

          // Remove target
          const targetMesh = this.targetMeshes.get(target.id);
          if (targetMesh) {
            this.scene.remove(targetMesh);
            this.targetMeshes.delete(target.id);
          }
          this.weaponSystem.removeTarget(target.id);
        }
      });
    });
  }
}

// Initialize the demo when page loads
window.addEventListener('DOMContentLoaded', () => {
  new LongbowDemo();
});
